import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CircleMinus, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getTradingLimitsIntradayAlgoTradingLimitsGet as getTradingLimits } from "@/client-admin/services.gen";
import { TradingLimitsResponse } from "@/client-admin/types.gen";
import { cn, getErrorDetails } from "@/lib/utils";
import { OverrideTradingLimitsDialog } from "./OverrideTradingLimitsDialog";
import {
  formatAmsterdamDateTime,
  formatTradingLimit,
  sameTradingLimit,
  todayInAmsterdam,
  TRADING_LIMIT_FIELDS,
  TRADING_LIMIT_GROUPS,
  TRADING_LIMIT_UNITS,
  TradingLimitField,
  tradingLimitLabel,
} from "./tradingLimits";

interface TradingLimitsPanelProps {
  deliveryDate: string;
}

export function TradingLimitsPanel({ deliveryDate }: TradingLimitsPanelProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["intraday-algo-trading-limits", deliveryDate],
    queryFn: () => getTradingLimits({ deliveryDate }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{getErrorDetails(error)}</AlertDescription>
      </Alert>
    );
  }

  const changedFields = TRADING_LIMIT_FIELDS.filter(
    (field) => !sameTradingLimit(data.effective[field], data.default[field]),
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <OverrideSummary data={data} changedCount={changedFields.length} />
        <OverrideTradingLimitsDialog
          deliveryDate={deliveryDate}
          effective={data.effective}
          defaults={data.default}
          canOverride={deliveryDate >= todayInAmsterdam()}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/2">Limit</TableHead>
              <TableHead className="text-right">Default</TableHead>
              <TableHead className="text-right">For {deliveryDate}</TableHead>
              <TableHead className="w-24">Unit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRADING_LIMIT_GROUPS.map((group) => (
              <Fragment key={group.name}>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell colSpan={4} className="py-2 text-left text-xs font-semibold text-muted-foreground">
                    {group.name}
                  </TableCell>
                </TableRow>
                {group.fields.map((field) => (
                  <LimitRow key={field} field={field} data={data} changed={changedFields.includes(field)} />
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function OverrideSummary({ data, changedCount }: { data: TradingLimitsResponse; changedCount: number }) {
  if (!data.overridden_by || !data.overridden_at) {
    return (
      <p className="text-sm text-muted-foreground">
        The client&apos;s default limits apply to <strong className="text-foreground">{data.delivery_date}</strong>.
      </p>
    );
  }
  const overriddenAt = formatAmsterdamDateTime(data.overridden_at);
  return (
    <div className="space-y-0.5 text-sm">
      <p>
        {changedCount === 0
          ? "Overridden back to the default limits"
          : `${changedCount} ${changedCount === 1 ? "limit differs" : "limits differ"} from the default`}{" "}
        for <strong>{data.delivery_date}</strong>.
      </p>
      <p className="text-muted-foreground">
        Last override by {data.overridden_by} at {overriddenAt}
      </p>
    </div>
  );
}

function LimitRow({
  field,
  data,
  changed,
}: {
  field: TradingLimitField;
  data: TradingLimitsResponse;
  changed: boolean;
}) {
  return (
    <TableRow className={cn(changed && "bg-primary/5 hover:bg-primary/10")}>
      <TableCell className={cn("text-left", changed && "border-l-2 border-l-primary")}>
        {tradingLimitLabel(field)}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        <LimitValue field={field} value={data.default[field]} />
      </TableCell>
      <TableCell className={cn("text-right tabular-nums", changed && "font-semibold")}>
        <LimitValue field={field} value={data.effective[field]} />
      </TableCell>
      <TableCell className="text-left text-muted-foreground">{TRADING_LIMIT_UNITS[field]}</TableCell>
    </TableRow>
  );
}

function LimitValue({ field, value }: { field: TradingLimitField; value: string | number | null }) {
  if (value === null) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground" title="Check turned off">
        <CircleMinus className="h-4 w-4" />
        Off
      </span>
    );
  }
  return <>{formatTradingLimit(field, value)}</>;
}
